import { Directive, inject } from '@angular/core';
import { IRequestCallbackFunction } from '@df/api/request/request.service';
import { IConfigData } from '@df/catalog/config/config.interfaces';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { FastCheckoutFormComponentDef } from '@df/checkout/fast/fast-checkout-form.component.def';
import { ApplePayService } from '@df/module-apple-pay/apple-pay.service';
import { Braintree } from '@df/module-braintree/braintree.namespace';
import { Giftcards } from '@df/module-giftcards/giftcards';
import { ICmsConfig } from '@icms/core/resource/config/icms-config';
import { ICmsConfigResource } from '@icms/core/resource/config/icms-config.resource';
import { GiftboxService } from '../../basket/giftbox/giftbox.service';
import { CMS_CONFIG_APPLEPAY, ICmsConfigApplePayDataContent } from '../../cms/config/apple-pay.cms-config';
import { BasketDecorator } from '../../customer/api/basket/basket.decorator';
import { ExponeaService } from '../../module/exponea/exponea.service';

@Directive()
export abstract class LocalFastCheckoutFormComponentDef extends FastCheckoutFormComponentDef {
    private showApplePay: string | undefined;
    protected override emitInitialGtmEvents = false;
    shippingTrayOpen = false;

    giftboxService = inject(GiftboxService);

    protected applePayService = inject(ApplePayService);

    protected exponeaService = inject(ExponeaService);

    protected cmsConfigResource = inject(ICmsConfigResource);

    override ngOnInit() {
        super.ngOnInit();

        this.cmsConfigResource.loadModelByUrl(CMS_CONFIG_APPLEPAY).then(() => {
            const config = this.cmsConfigResource.getModelByUrl(CMS_CONFIG_APPLEPAY) as ICmsConfig<
                IConfigData<ICmsConfigApplePayDataContent>
            >;
            this.showApplePay = config?.data?.content?.showApplePay;
        });
    }

    get boxItemsCount(): number {
        return this.giftboxService.itemsCount;
    }

    /**
     * Customer basket shorthand
     */
    override get basket() {
        return <BasketDecorator>this.app.customer.basket;
    }

    get canAddGiftbox(): boolean {
        // checks if:
        // 1.  checkout loaded and totals are loaded
        // 2.  giftboxes are enabled on BE
        // 3.  gifting product was succesfully loaded
        return !!(
            this.backendData &&
            this.backendData?.totals?.shipping &&
            this.app.data.giftboxesEnabled &&
            this.giftboxService.getGiftingProduct() &&
            this.giftboxService.isGiftingAvailableForBasket
        );
    }

    get displayApplePay() {
        return !!(
            this.showApplePay &&
            !this.isFree &&
            this.paymentMethodByCode(Braintree.Methods.ApplePay) &&
            this.backendData &&
            !this.loadStatus.busy &&
            this.applePayService.isAvailable &&
            this.applePayService.isSupportedVersion(3)
        );
    }

    protected override load(): void {
        this.giftboxService.getBoxItems();

        super.load();
    }

    trackByGiftcard(index: number, giftcard: Giftcards.IAddedGiftcardData) {
        return giftcard.code;
    }

    override submitWithData(
        data: Checkout.ISubmissionData,
        onSuccess?: IRequestCallbackFunction,
        onError?: IRequestCallbackFunction,
        force = false
    ) {
        const expressPaymentMethods = [Braintree.Methods.ApplePay, Braintree.Methods.GooglePay] as string[];

        if (expressPaymentMethods.indexOf(`${data.paymentMethod?.method}`) !== -1) {
            // identify customer for express payment methods
            if (!this.customer.model.isLoggedIn && data?.email) {
                this.exponeaService.identifyCustomer(data.email);
            }

            data.paymentMethod.additional_data = {
                ...data.paymentMethod.additional_data,
                express: true
            };
        }

        return super.submitWithData(data, onSuccess, onError, force);
    }
}
